import { useNavigate } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { BarRow, MetaText, MetricTile } from "@/components/plan-audit/AuditPrimitives";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { type ReportingData, useReporting } from "@/hooks/use-plan-audit";
import { RECOMMENDATION_TARGET_OPTIONS } from "@/lib/enums";
import {
  bandColorVar,
  deltaToneVar,
  formatDelta,
  formatMonth,
  formatScore,
  NO_VALUE,
  scoreBand,
} from "@/lib/plan-audit-format";

/**
 * Reporting — how good the Solution tool's output is, and what the evidence says
 * to change.
 *
 * Everything measures FIRST-PASS quality: the score a plan got before anyone
 * edited it. That is the only number that says something about the generator
 * rather than about how hard a human worked afterwards.
 *
 * The recommendations panel is the point of the whole exercise. The source plan's
 * own audit flagged (GAP-02) that its pipeline stopped at a trend view and never
 * fed anything back into the Solution tool's prompts, schema or validators —
 * "otherwise we're just producing scorecards nobody acts on". Capturing the
 * recommendation is the first half of closing that loop; acting on it is still a
 * human step outside this app, and the panel says so.
 */
export function ReportingView() {
  const { data, isLoading, isError, error, refetch } = useReporting();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }
  if (isError) return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  if (!data) return <ErrorState message="Could not load reporting" />;

  return (
    <>
      {data.sparse && <SparseNotice />}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          label="Median first-pass score"
          value={formatScore(data.kpis.medianFirstPassScore)}
          note={
            data.kpis.medianDelta90Days === null
              ? "no earlier period to compare"
              : `${formatDelta(data.kpis.medianDelta90Days)} over 90 days`
          }
          noteTone={deltaToneVar(data.kpis.medianDelta90Days)}
        />
        <MetricTile
          label="Plans audited"
          value={String(data.kpis.plansAudited)}
          note={`across ${data.kpis.solutionsCovered} solution${data.kpis.solutionsCovered === 1 ? "" : "s"}`}
        />
        <MetricTile
          label="Flags per plan"
          value={data.kpis.flagsPerPlan === null ? NO_VALUE : data.kpis.flagsPerPlan.toFixed(1)}
          note={
            data.kpis.flagsPerPlanDelta90Days === null
              ? "no earlier period to compare"
              : `${formatDelta(data.kpis.flagsPerPlanDelta90Days)} over 90 days`
          }
          // Fewer flags is better, so the tone inverts. The number cannot know
          // that; the caller says which direction is good news.
          noteTone={deltaToneVar(data.kpis.flagsPerPlanDelta90Days, false)}
        />
        <MetricTile
          label="Sections below 6"
          value={
            data.kpis.sectionsBelowBandPct === null
              ? NO_VALUE
              : `${Math.round(data.kpis.sectionsBelowBandPct)}%`
          }
          note={
            data.kpis.sectionsBelowBandPctPrior === null
              ? "first measured period"
              : `was ${Math.round(data.kpis.sectionsBelowBandPctPrior)}% before`
          }
          noteTone={deltaToneVar(
            data.kpis.sectionsBelowBandPctPrior === null || data.kpis.sectionsBelowBandPct === null
              ? null
              : data.kpis.sectionsBelowBandPct - data.kpis.sectionsBelowBandPctPrior,
            false,
          )}
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <TrendChart data={data} />
        <WeakSections data={data} />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_1fr_1.2fr]">
        <GroupAverages data={data} />
        <FlagMix data={data} />
        <Recommendations data={data} />
      </div>
    </>
  );
}

function SparseNotice() {
  return (
    <div
      className="mb-4 flex gap-2 rounded-lg border p-3"
      style={{ background: "var(--info-bg)", borderColor: "var(--info)" }}
    >
      <Info
        aria-hidden="true"
        size={16}
        className="mt-0.5 shrink-0"
        style={{ color: "var(--info)" }}
      />
      <p className="text-xs leading-relaxed" style={{ color: "var(--info-fg)" }}>
        <b>Not much history yet.</b> These figures are computed from the runs recorded so far. Below
        a few months of audits the movement columns describe noise as much as trend — they are shown
        so the shape of the page is reviewable, not because the direction is meaningful.
      </p>
    </div>
  );
}

function TrendChart({ data }: { data: ReportingData }) {
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h2>First-pass score by month</h2>
        <MetaText>Before anyone edited the plan</MetaText>
      </div>
      {data.trend.length === 0 ? (
        <p className="text-[13px] text-[var(--fg-3)]">No scored first-pass runs recorded yet.</p>
      ) : (
        <div className="flex h-52 items-end gap-3">
          {data.trend.map((point) => {
            const band = scoreBand(point.meanScore);
            return (
              <div key={point.month} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <span
                  className="text-[11px] font-semibold tabular-nums"
                  style={{ color: bandColorVar(band) }}
                >
                  {point.meanScore.toFixed(1)}
                </span>
                <div
                  className="w-full rounded-t-md"
                  style={{
                    height: `${point.meanScore * 10}%`,
                    minHeight: 4,
                    background: bandColorVar(band),
                  }}
                />
                <span className="text-[11px] font-medium text-[var(--fg-2)]">
                  {formatMonth(point.month)}
                </span>
                <MetaText>
                  {point.runCount} run{point.runCount === 1 ? "" : "s"}
                </MetaText>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WeakSections({ data }: { data: ReportingData }) {
  const navigate = useNavigate();
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h2>Recurring weak sections</h2>
        <MetaText>Scored below 6</MetaText>
      </div>
      {data.weakSections.length === 0 ? (
        <p className="text-[13px] text-[var(--fg-3)]">
          No section has scored below 6 in a first-pass run.
        </p>
      ) : (
        <div>
          {data.weakSections.map((section) => (
            <BarRow
              key={section.name}
              label={section.name}
              value={`${section.weakCount}/${section.totalCount}`}
              fraction={section.weakCount / Math.max(1, section.totalCount)}
              tone={
                section.weakCount / Math.max(1, section.totalCount) > 0.7
                  ? "var(--danger)"
                  : "var(--warning)"
              }
              onClick={() => void navigate({ to: "/solutions" })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupAverages({ data }: { data: ReportingData }) {
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h2>Average by group</h2>
      </div>
      {data.groupAverages.every((g) => g.mean === null) ? (
        <p className="text-[13px] text-[var(--fg-3)]">No section scores recorded yet.</p>
      ) : (
        <div>
          {data.groupAverages.map((group) => (
            <BarRow
              key={group.group}
              label={group.group}
              value={formatScore(group.mean)}
              fraction={(group.mean ?? 0) / 10}
              tone={bandColorVar(scoreBand(group.mean))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FlagMix({ data }: { data: ReportingData }) {
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h2>Flag mix</h2>
        <MetaText>By failure pattern</MetaText>
      </div>
      {data.flagMix.length === 0 ? (
        <p className="text-[13px] text-[var(--fg-3)]">No flags raised in a first-pass run.</p>
      ) : (
        <div>
          {data.flagMix.map((entry) => (
            <BarRow
              key={entry.category}
              label={entry.label}
              value={`${entry.count} · ${Math.round(entry.percentage)}%`}
              fraction={entry.percentage / 100}
              tone={entry.percentage > 40 ? "var(--danger)" : "var(--warning)"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Recommendations({ data }: { data: ReportingData }) {
  return (
    <div className="pa-card">
      <div className="pa-card-head">
        <h2>What to change</h2>
        <MetaText>In the Solution tool</MetaText>
      </div>
      {data.recommendations.length === 0 ? (
        <p className="text-[13px] text-[var(--fg-3)]">
          No recommendations recorded from the evidence yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {data.recommendations.map((rec) => (
            <div key={rec.id} className="flex gap-2.5">
              <span
                className="mt-0.5 h-fit shrink-0 rounded px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase"
                style={{ background: "var(--bg-accent)", color: "var(--brand-accent-hover)" }}
              >
                {RECOMMENDATION_TARGET_OPTIONS.find((o) => o.value === rec.target)?.label ??
                  rec.target}
              </span>
              <p className="text-xs leading-relaxed text-[var(--fg-2)]">{rec.body}</p>
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 border-t border-[var(--border-1)] pt-3 text-[11px] leading-relaxed text-[var(--fg-3)]">
        Recording these closes half the loop. Applying them to the Solution tool is a human step
        outside this app — nothing here writes back.
      </p>
    </div>
  );
}
